import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { api, TimeEntry } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

export default function Review() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const data = await api.getReviewEntries();
      setEntries(data);
    } catch (error) {
      console.error("Failed to load review entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const selectAll = () => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map(e => e.id)));
    }
  };

  const handleApprove = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    try {
      const result = await api.approveTimeEntries(Array.from(selectedIds));
      toast({
        title: "Entries Approved",
        description: `Successfully approved ${result.updated} time entries.`,
      });
      setSelectedIds(new Set());
      await loadEntries();
    } catch (error: any) {
      toast({
        title: "Approval Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (selectedIds.size === 0 || !rejectNote.trim()) return;
    setProcessing(true);
    try {
      const result = await api.rejectTimeEntries(Array.from(selectedIds), rejectNote);
      toast({
        title: "Entries Rejected",
        description: `Rejected ${result.updated} time entries.`,
      });
      setSelectedIds(new Set());
      setRejectNote("");
      setShowRejectDialog(false);
      await loadEntries();
    } catch (error: any) {
      toast({
        title: "Rejection Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const groupedByUser = entries.reduce((acc, entry) => {
    const userId = entry.user_id;
    if (!acc[userId]) {
      acc[userId] = [];
    }
    acc[userId].push(entry);
    return acc;
  }, {} as Record<string, TimeEntry[]>);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-page-title">Review & Approvals</h1>
            <p className="text-muted-foreground">Review and approve submitted time entries.</p>
          </div>
          
          {entries.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={selectAll}
                data-testid="button-select-all"
              >
                {selectedIds.size === entries.length ? "Deselect All" : "Select All"}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRejectDialog(true)}
                disabled={selectedIds.size === 0 || processing}
                data-testid="button-reject"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject ({selectedIds.size})
              </Button>
              <Button
                onClick={handleApprove}
                disabled={selectedIds.size === 0 || processing}
                data-testid="button-approve"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve ({selectedIds.size})
              </Button>
            </div>
          )}
        </div>

        {entries.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Clock className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No Pending Reviews</p>
              <p className="text-sm">All submitted time entries have been reviewed.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByUser).map(([userId, userEntries]) => {
              const totalHours = userEntries.reduce((sum, e) => sum + e.minutes, 0) / 60;
              
              return (
                <Card key={userId} className="border-t-4 border-t-primary shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{userEntries[0]?.users?.name ?? userId.substring(0, 8) + "..."}</CardTitle>
                      <span className="text-sm text-muted-foreground">
                        {userEntries.length} entries • {totalHours.toFixed(2)} hours
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {userEntries.map(entry => {
                        const hours = entry.minutes / 60;
                        const isSelected = selectedIds.has(entry.id);
                        
                        return (
                          <div
                            key={entry.id}
                            className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                              isSelected ? "bg-primary/5 border-primary/20" : "bg-muted/30 hover:bg-muted/50"
                            }`}
                            data-testid={`row-review-entry-${entry.id}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelection(entry.id)}
                              data-testid={`checkbox-entry-${entry.id}`}
                            />
                            <div className="w-20 text-sm font-medium">
                              {format(parseISO(entry.entry_date), "MMM d")}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">
                                {entry.accounts?.account_name || "No Account"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {entry.activity_codes?.name || entry.activity_code}
                                {entry.notes && ` • ${entry.notes}`}
                              </div>
                            </div>
                            <div className="font-mono font-bold">
                              {hours.toFixed(2)}h
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Time Entries</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                You are about to reject {selectedIds.size} time entries. Please provide a reason.
              </p>
              <Textarea
                placeholder="Reason for rejection..."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                data-testid="textarea-reject-note"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectNote.trim() || processing}
                data-testid="button-confirm-reject"
              >
                {processing ? "Rejecting..." : "Reject Entries"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
